
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";

import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import EmailVerificationScreen from "@/components/auth/EmailVerificationScreen";
import MFAVerification from "@/components/auth/MFAVerification";

const LoginPage = () => {
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Track first loading completed to avoid flashing redirects
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setInitialLoadComplete(true);
    }
  }, [isLoading]);

  // Only redirect if authentication state is confirmed and user is authenticated
  useEffect(() => {
    if (initialLoadComplete && isAuthenticated && !showMFAVerification && !isEmailSent) {
      console.log("User is authenticated, redirecting to home page");
      navigate("/");
    }
  }, [isAuthenticated, initialLoadComplete, showMFAVerification, isEmailSent, navigate]);

  const handleMFASuccess = () => {
    console.log("MFA verification successful, redirecting to home");
    setShowMFAVerification(false);
    navigate("/");
  };

  const handleMFACancel = () => {
    console.log("MFA verification canceled");
    setShowMFAVerification(false);
  };

  const handleForgotPassword = () => {
    // To be implemented
    console.log("Forgot password clicked");
  };

  const handleLoginResult = async (result: any) => {
    console.log("Login result received:", result);
    if (result?.mfaRequired) {
      console.log("MFA required, showing verification screen");
      setShowMFAVerification(true);
    } else {
      // No MFA required, redirect to home
      console.log("No MFA required, redirecting to home");
      navigate("/");
    }
  };

  // Don't render anything during initial loading
  if (isLoading && !initialLoadComplete) {
    return null;
  }

  // Show email verification screen
  if (isEmailSent) {
    return <EmailVerificationScreen onBackToLogin={() => setIsEmailSent(false)} />;
  }

  // Show MFA verification screen
  if (showMFAVerification) {
    return (
      <div className="min-h-screen bg-tokscript-bg flex flex-col items-center justify-center p-4">
        <div className="text-center mb-8">
          <div className="text-3xl font-bold">
            <span className="text-white">tok</span>
            <span className="text-tokscript-cta">script</span>
          </div>
          <p className="text-gray-400">Authentication verification required</p>
        </div>
        <MFAVerification onSuccess={handleMFASuccess} onCancel={handleMFACancel} />
      </div>
    );
  }

  // Show login/signup form
  return (
    <div className="min-h-screen bg-tokscript-bg flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="text-3xl font-bold">
              <span className="text-white">tok</span>
              <span className="text-tokscript-cta">script</span>
            </div>
          </div>
          <p className="text-gray-400">Archive your TikTok collections</p>
        </div>
        
        <Card className="bg-tokscript-card border-gray-800">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Welcome</CardTitle>
            <CardDescription className="text-center">Sign in or create an account to continue</CardDescription>
          </CardHeader>
          
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <CardContent>
                <LoginForm 
                  onForgotPassword={handleForgotPassword}
                  onLoginSuccess={handleLoginResult}
                />
              </CardContent>
            </TabsContent>
            
            <TabsContent value="signup">
              <CardContent>
                <SignupForm onEmailSent={() => setIsEmailSent(true)} />
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
